import { useLiveQuery } from 'dexie-react-hooks'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  FileText,
  MapPin,
  MessageCircle,
  Package,
  Truck,
  User,
} from 'lucide-react'
import { useEffect, useState } from 'react'

import { StatusBadge } from '@/components/StatusBadge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { useApp } from '@/context'
import {
  addActivitate,
  advanceComanda,
  genereazaFactura,
  getClient,
  getComanda,
  getMasini,
  getSoferi,
  setSoferMasina,
} from '@/db/queries'
import {
  fmtData,
  fmtDataRelativa,
  fmtDataScurt,
  fmtKg,
  fmtOra,
  fmtRON,
  fmtSuma,
  statusMeta,
  statusUrmator,
} from '@/lib/format'

/**
 * Full order detail in a right-side sheet. Reads live via `useLiveQuery`, so
 * advancing the funnel, assigning a driver, or generating an invoice all
 * update the panel in place. Deep-linkable: `Comenzi` opens it by id.
 */
export interface OrderDrawerProps {
  comandaId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const EASE = [0.16, 1, 0.3, 1] as const

export function OrderDrawer({ comandaId, open, onOpenChange }: OrderDrawerProps) {
  const { openOn } = useApp()

  const comanda = useLiveQuery(() => (comandaId ? getComanda(comandaId) : undefined), [comandaId])
  const client = useLiveQuery(
    () => (comanda?.clientId ? getClient(comanda.clientId) : undefined),
    [comanda?.clientId],
  )
  const soferi = useLiveQuery(() => getSoferi(), []) ?? []
  const masini = useLiveQuery(() => getMasini(), []) ?? []

  const [nota, setNota] = useState<string | null>(null)
  const [assignOpen, setAssignOpen] = useState(false)
  const [soferSel, setSoferSel] = useState('')
  const [masinaSel, setMasinaSel] = useState('')

  // reset transient panel UI whenever we point at a different order
  useEffect(() => {
    setAssignOpen(false)
    setNota(null)
  }, [comandaId])

  useEffect(() => {
    setSoferSel(comanda?.soferId ?? '')
    setMasinaSel(comanda?.masinaId ?? '')
  }, [comanda?.soferId, comanda?.masinaId])

  const flash = (msg: string) => {
    setNota(msg)
    window.setTimeout(() => setNota((cur) => (cur === msg ? null : cur)), 2800)
  }

  const sofer = comanda?.soferId ? soferi.find((s) => s.id === comanda.soferId) : undefined
  const masina = comanda?.masinaId ? masini.find((m) => m.id === comanda.masinaId) : undefined

  const soferiOptiuni = soferi.filter((s) => s.status === 'disponibil' || s.id === comanda?.soferId)
  const masiniOptiuni = masini.filter(
    (m) => m.status === 'disponibilă' || m.id === comanda?.masinaId,
  )

  const doAdvance = () => {
    if (comanda) void advanceComanda(comanda.id)
  }
  const doAssign = async () => {
    if (!comanda || !soferSel || !masinaSel) return
    await setSoferMasina(comanda.id, soferSel, masinaSel)
    setAssignOpen(false)
    flash('Șofer și mașină atribuite comenzii.')
  }
  const doFactura = async () => {
    if (!comanda) return
    const id = await genereazaFactura(comanda.id)
    if (id) flash('Factură generată și trimisă la ANAF e-Factura.')
  }
  const doWhatsApp = async () => {
    if (!comanda) return
    await addActivitate({
      tip: 'mesaj',
      icon: 'MessageCircle',
      text: `Confirmare trimisă pe WhatsApp — ${client?.nume ?? comanda.clientId}`,
    })
    flash(`Confirmare trimisă pe WhatsApp către ${client?.nume ?? 'client'}.`)
  }
  const veziFactura = () => {
    if (!comanda) return
    onOpenChange(false)
    openOn('facturare', comanda.id)
  }

  const next = comanda ? statusUrmator(comanda.status) : null
  const meta = comanda ? statusMeta[comanda.status] : null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 border-l-hairline p-0 sm:max-w-[520px]">
        {!comanda ? (
          <div className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground">
            Se încarcă comanda…
          </div>
        ) : (
          <>
            {/* ---- header ---- */}
            <div
              className="relative shrink-0 border-b border-hairline px-6 pt-6 pb-5"
              style={{
                backgroundImage: `radial-gradient(120% 130% at 100% 0%, ${meta?.bg} 0%, transparent 60%)`,
              }}
            >
              <SheetDescription className="nums font-mono text-xs tracking-wide text-muted-foreground">
                Comanda {comanda.id.replace('cmd-', '#')}
              </SheetDescription>
              <SheetTitle className="mt-1 pr-8 font-display text-xl leading-tight">
                {client?.nume ?? comanda.clientId}
              </SheetTitle>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <StatusBadge status={comanda.status} />
                {client && (
                  <span className="inline-flex items-center gap-1 text-xs capitalize text-muted-foreground">
                    <Building2 className="size-3" />
                    {client.tip} · {client.localitate}
                  </span>
                )}
                {comanda.codUIT && (
                  <span className="nums inline-flex items-center gap-1 rounded-md bg-tomato-soft px-1.5 py-0.5 font-mono text-[11px] text-tomato">
                    <Truck className="size-3" />
                    UIT {comanda.codUIT}
                  </span>
                )}
              </div>
            </div>

            {/* ---- scrollable body ---- */}
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              {/* address + dates */}
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="mt-0.5 size-4 shrink-0 text-taupe" />
                <span className="text-ink-soft">{comanda.adresaLivrare || '—'}</span>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-hairline bg-secondary/50 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Plasată
                  </p>
                  <p className="nums mt-0.5 font-mono text-sm">{fmtData(comanda.dataComanda)}</p>
                </div>
                <div className="rounded-lg border border-hairline bg-amber-soft/50 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Livrare
                  </p>
                  <p className="nums mt-0.5 font-mono text-sm text-amber-ink">
                    {fmtDataRelativa(comanda.dataLivrare)} · {fmtData(comanda.dataLivrare)}
                  </p>
                </div>
              </div>

              {/* line items */}
              <div className="mt-6">
                <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Package className="size-3.5" />
                  Produse
                </h3>
                <div className="overflow-hidden rounded-lg border border-hairline">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-hairline bg-secondary/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-2 text-left font-medium">Produs</th>
                        <th className="px-3 py-2 text-right font-medium">Cant.</th>
                        <th className="px-3 py-2 text-right font-medium">Preț/kg</th>
                        <th className="px-3 py-2 text-right font-medium">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comanda.linii.map((l, i) => (
                        <tr
                          key={`${l.produsId}-${i}`}
                          className="border-b border-hairline/60 last:border-0"
                        >
                          <td className="px-3 py-2">{l.nume ?? l.produsId}</td>
                          <td className="nums px-3 py-2 text-right font-mono text-muted-foreground">
                            {fmtKg(l.cantitateKg)}
                          </td>
                          <td className="nums px-3 py-2 text-right font-mono text-muted-foreground">
                            {fmtSuma(l.pretKg)}
                          </td>
                          <td className="nums px-3 py-2 text-right font-mono">
                            {fmtSuma(l.cantitateKg * l.pretKg)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-hairline bg-secondary/40 font-medium">
                        <td className="px-3 py-2.5 text-xs uppercase tracking-wide text-muted-foreground">
                          Total
                        </td>
                        <td className="nums px-3 py-2.5 text-right font-mono">
                          {fmtKg(comanda.totalKg)}
                        </td>
                        <td />
                        <td className="nums px-3 py-2.5 text-right font-mono font-semibold text-amber-ink">
                          {fmtRON(comanda.totalRON)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* fleet */}
              <div className="mt-6">
                <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Truck className="size-3.5" />
                  Livrare
                </h3>
                {sofer || masina ? (
                  <div className="flex flex-wrap gap-2">
                    {sofer && (
                      <span className="inline-flex items-center gap-1.5 rounded-md bg-pine-soft px-2.5 py-1 text-sm text-pine">
                        <User className="size-3.5" />
                        {sofer.nume}
                      </span>
                    )}
                    {masina && (
                      <span className="nums inline-flex items-center gap-1.5 rounded-md bg-secondary px-2.5 py-1 font-mono text-sm text-secondary-foreground">
                        <Truck className="size-3.5" />
                        {masina.nrInmatriculare}
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Niciun șofer atribuit încă.</p>
                )}
              </div>

              {/* timeline */}
              <div className="mt-6">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Istoric status
                </h3>
                <ol className="pl-0.5">
                  {comanda.timeline.map((t, i) => {
                    const tm = statusMeta[t.status]
                    const last = i === comanda.timeline.length - 1
                    return (
                      <li key={`${t.status}-${i}`} className="flex gap-3 pb-4 last:pb-0">
                        <div className="flex flex-col items-center">
                          <span
                            className="z-10 mt-0.5 size-3 shrink-0 rounded-full ring-4 ring-card"
                            style={{ backgroundColor: tm.dot }}
                          />
                          {!last && <span className="w-px flex-1 bg-hairline" />}
                        </div>
                        <div className="-mt-0.5">
                          <p className="text-sm font-medium" style={{ color: tm.fg }}>
                            {tm.label}
                          </p>
                          <p className="nums font-mono text-xs text-muted-foreground">
                            {fmtDataScurt(t.ora)} · {fmtOra(t.ora)}
                          </p>
                        </div>
                      </li>
                    )
                  })}
                </ol>
              </div>
            </div>

            {/* ---- footer / actions ---- */}
            <div className="shrink-0 border-t border-hairline bg-card px-6 py-4">
              <AnimatePresence>
                {nota && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: 8, height: 0 }}
                    transition={{ duration: 0.28, ease: EASE }}
                    className="mb-3 flex items-center gap-2 rounded-md bg-pine-soft px-3 py-2 text-sm text-pine"
                  >
                    <CheckCircle2 className="size-4 shrink-0" />
                    {nota}
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence initial={false}>
                {assignOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    className="overflow-hidden"
                  >
                    <div className="mb-3 grid gap-2 rounded-lg border border-hairline bg-secondary/40 p-3">
                      <Select value={soferSel} onValueChange={setSoferSel}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Alege șofer" />
                        </SelectTrigger>
                        <SelectContent>
                          {soferiOptiuni.length === 0 && (
                            <SelectItem value="—" disabled>
                              Niciun șofer disponibil
                            </SelectItem>
                          )}
                          {soferiOptiuni.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.nume}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={masinaSel} onValueChange={setMasinaSel}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Alege mașină" />
                        </SelectTrigger>
                        <SelectContent>
                          {masiniOptiuni.length === 0 && (
                            <SelectItem value="—" disabled>
                              Nicio mașină disponibilă
                            </SelectItem>
                          )}
                          {masiniOptiuni.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.nrInmatriculare} · {fmtKg(m.capacitateKg)}
                              {m.frigorific ? ' · frig.' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="pine"
                        size="sm"
                        disabled={!soferSel || !masinaSel}
                        onClick={doAssign}
                      >
                        Confirmă atribuirea
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* primary action, driven by status */}
              {comanda.status === 'Facturat' && comanda.facturaId ? (
                <Button className="w-full" onClick={veziFactura}>
                  <FileText className="size-4" />
                  Vezi factura
                </Button>
              ) : comanda.status === 'Livrat' ? (
                <Button variant="pine" className="w-full" onClick={doFactura}>
                  <FileText className="size-4" />
                  Generează factură
                </Button>
              ) : next ? (
                <Button className="w-full" onClick={doAdvance}>
                  {comanda.status === 'Nou' ? 'Confirmă comanda' : `Avansează → ${next}`}
                  <ArrowRight className="size-4" />
                </Button>
              ) : null}

              <div className="mt-2 grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAssignOpen((v) => !v)}
                  disabled={comanda.status === 'Facturat'}
                >
                  <User className="size-4" />
                  Atribuie șofer
                </Button>
                <Button variant="outline" size="sm" onClick={doWhatsApp}>
                  <MessageCircle className="size-4" />
                  Confirmare WhatsApp
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
